import React, { useEffect, useState } from "react";
import LayoutAdmin from "../components/LayoutAdmin";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Snackbar,
    Alert,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { MenuItem, Select, InputLabel, FormControl } from "@mui/material";

import axiosExt from '../axios';
import { API_BASE_URL } from "../config.ts";
import { useTranslation } from "react-i18next";

import { AccountingTranslation } from "../types";

const AccountingTranslationsPage: React.FC = () => {
    const { t } = useTranslation();

    const [rows, setRows] = useState<AccountingTranslation[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ message: string; severity: "success" | "error" } | null>(null);

    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [editing, setEditing] = useState<AccountingTranslation | null>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await axiosExt.get(`${API_BASE_URL}/AccountingTranslations`);
            setRows(res.data);
        } catch (err: any) {
            setError(err.response?.data || "Failed to load translations.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleEdit = (row?: AccountingTranslation) => {
        setEditing(row || { sourceType: "", sourceValue: "", accountingName: "", accountingCode: "", externalSystem: "" });
        setOpenDialog(true);
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm("Are you sure you want to delete this translation?")) return;
        try {
            await axiosExt.delete(`${API_BASE_URL}/AccountingTranslations/${id}`);
            setSnackbar({ message: "Deleted successfully", severity: "success" });
            await fetchData();
        } catch (err: any) {
            setSnackbar({ message: err.response?.data || "Failed to delete", severity: "error" });
        }
    };

    const handleSave = async () => {
        if (!editing) return;

        // --- Local Validation ---
        const errors: Record<string, string> = {};
        if (!editing.sourceType) errors.sourceType = "Source Type is required";
        if (!editing.sourceValue?.trim()) errors.sourceValue = "Source Value is required";
        if (!editing.accountingName?.trim()) errors.accountingName = "Accounting Name is required";

        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) return; // stop here if errors exist

        setSaving(true);
        try {
            if (editing.id) {
                await axiosExt.put(`${API_BASE_URL}/AccountingTranslations/${editing.id}`, editing);
                setSnackbar({ message: "Updated successfully", severity: "success" });
            } else {
                await axiosExt.post(`${API_BASE_URL}/AccountingTranslations`, editing);
                setSnackbar({ message: "Created successfully", severity: "success" });
            }
            setOpenDialog(false);
            await fetchData();
        } catch (err: any) {
            const msg = err.response?.data || "Failed to save changes.";
            setSnackbar({ message: msg, severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    const columns: GridColDef[] = [
        { field: "sourceType", headerName: t("Source Type"), flex: 1 },
        { field: "sourceValue", headerName: t("Source Value"), flex: 1 },
        { field: "accountingName", headerName: t("Accounting Name"), flex: 1 },
        {
            field: "actions",
            headerName: t("Actions"),
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="contained" size="small" onClick={() => handleEdit(params.row)}>{t("Edit")}</Button>
                    <Button variant="contained" size="small" color="error" onClick={() => handleDelete(params.row.id)}>{t("Delete")}</Button>
                </Box>
            ),
            width: 180,
        },
    ];

    return (
        <LayoutAdmin>
            <Box sx={{ p: 2, width: "100%", maxWidth: "100vw" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h5">{t("Accounting Translations")}</Typography>
                    <Button variant="contained" onClick={() => handleEdit()}>{t("Add")} </Button>
                </Box>
                {loading ? (
                    <Box sx={{ textAlign: "center", mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <>
                        <Box
                            sx={{
                               width: "calc(100vw - 300px)",              // full viewport width - 
                            }}
                        >
                            <Box
                                sx={{
                                    height: "calc(100vh - 220px)", // adjusts for header/footer height
                                    width: "100%",
                                }}
                            >
                                <DataGrid
                                    rows={rows}
                                    columns={columns}
                                    getRowId={(r) => r.id!}
                                    pageSizeOptions={[25, 50, 100, 200]}
                                    disableRowSelectionOnClick
                                    sx={{
                                        "& .MuiDataGrid-columnHeaders": {
                                            backgroundColor: "#f4f4f4",
                                            fontWeight: 600,
                                        },
                                    }}
                                />
                            </Box>
                        </Box>

                    </>
                )}
            </Box>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editing?.id ? t("Edit") : t("New")}</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <FormControl fullWidth required error={!!fieldErrors.sourceType}>
                        <InputLabel id="source-type-label">{t("Source Type")}</InputLabel>
                        <Select
                            labelId="source-type-label"
                            label={t("Source Type")}
                            value={editing?.sourceType || ""}
                            onChange={(e) => setEditing({ ...editing!, sourceType: e.target.value })}
                        >
                            <MenuItem value="Tax Type">{t("Tax Type")}</MenuItem>
                            <MenuItem value="Service Name">{t("Service Name")}</MenuItem>
                        </Select>
                        {fieldErrors.sourceType && (
                            <Typography variant="caption" color="error">{fieldErrors.sourceType}</Typography>
                        )}
                    </FormControl>
                    <TextField
                        label={t("Source Value")}
                        value={editing?.sourceValue || ""}
                        onChange={(e) => setEditing({ ...editing!, sourceValue: e.target.value })}
                        required
                        error={!!fieldErrors.sourceValue}
                        helperText={fieldErrors.sourceValue}
                    />
                    <TextField
                        label={t("Accounting Name")}
                        value={editing?.accountingName || ""}
                        onChange={(e) => setEditing({ ...editing!, accountingName: e.target.value })}
                        required
                        error={!!fieldErrors.accountingName}
                        helperText={fieldErrors.accountingName}
                    />
                    <TextField
                        label={t("Accounting Code")}
                        value={editing?.accountingCode || ""}
                        onChange={(e) => setEditing({ ...editing!, accountingCode: e.target.value })}
                    />
                    <TextField
                        label={t("External System")}
                        value={editing?.externalSystem || ""}
                        onChange={(e) => setEditing({ ...editing!, externalSystem: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>{t("Cancel")}</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? t("Saving...") : t("Save")}
                    </Button>
                </DialogActions>
            </Dialog>

            {snackbar && (
                <Snackbar
                    open={!!snackbar}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            )}
        </LayoutAdmin>
    );
};

export default AccountingTranslationsPage;
